import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import {
  DEFAULT_FIELD_ORDER,
  DEFAULT_DROP_TOKENS_MODE,
  DEFAULT_FIELDS_ENABLED,
  DEFAULT_SFX_FIELD_ORDER,
  DEFAULT_SFX_FIELDS_ENABLED,
} from '@music-vine/setlist';

import type {
  SimilarityWeights,
  SimilarityField,
  DropTokensMode,
  SfxSimilarityWeights,
  SfxSimilarityField,
} from '@music-vine/setlist';

/**
 * Similarity Weight Debug Atoms
 * These atoms allow admins to override similarity search weights
 * for testing and debugging purposes.
 */

// Boolean to enable/disable weight overrides
export const similarityWeightsEnabledAtom = atomWithStorage<boolean>(
  'debugSimilarityWeightsEnabled',
  false
);
similarityWeightsEnabledAtom.debugLabel = 'similarityWeightsEnabledAtom';

// Persisted atom for weight overrides
export const similarityWeightsOverrideAtom =
  atomWithStorage<SimilarityWeights | null>('debugSimilarityWeightsV4', null);
similarityWeightsOverrideAtom.debugLabel = 'similarityWeightsOverrideAtom';

// Persisted atom for field order
export const similarityFieldOrderAtom = atomWithStorage<SimilarityField[]>(
  'debugSimilarityFieldOrderV4',
  DEFAULT_FIELD_ORDER
);
similarityFieldOrderAtom.debugLabel = 'similarityFieldOrderAtom';

// Persisted atom for which fields are enabled (all enabled by default)
export const similarityFieldsEnabledAtom = atomWithStorage<
  Record<SimilarityField, boolean>
>('debugSimilarityFieldsEnabledV4', {
  keywordsW3: true,
  keywordsW2: true,
  keywordsW1: true,
});
similarityFieldsEnabledAtom.debugLabel = 'similarityFieldsEnabledAtom';

// Persisted atom for drop tokens mode
export const similarityDropTokensModeAtom = atomWithStorage<DropTokensMode>(
  'debugSimilarityDropTokensMode',
  DEFAULT_DROP_TOKENS_MODE
);
similarityDropTokensModeAtom.debugLabel = 'similarityDropTokensModeAtom';

// Derived atom that returns overrides (if enabled) or null (use defaults)
export const activeSimilarityWeightsAtom = atom((get) => {
  const enabled = get(similarityWeightsEnabledAtom);
  const overrides = get(similarityWeightsOverrideAtom);
  return enabled && overrides ? overrides : null;
});
activeSimilarityWeightsAtom.debugLabel = 'activeSimilarityWeightsAtom';

// Derived atom for active field order (returns override or null for default)
export const activeSimilarityFieldOrderAtom = atom((get) => {
  const enabled = get(similarityWeightsEnabledAtom);
  const fieldOrder = get(similarityFieldOrderAtom);
  // Only return custom order if enabled and order differs from default
  if (!enabled) {
    return null;
  }
  const isDefault =
    fieldOrder.length === DEFAULT_FIELD_ORDER.length &&
    fieldOrder.every((field, i) => field === DEFAULT_FIELD_ORDER[i]);
  return isDefault ? null : fieldOrder;
});
activeSimilarityFieldOrderAtom.debugLabel = 'activeSimilarityFieldOrderAtom';

// Derived atom for active drop tokens mode (returns override or null for default)
export const activeSimilarityDropTokensModeAtom = atom((get) => {
  const enabled = get(similarityWeightsEnabledAtom);
  const mode = get(similarityDropTokensModeAtom);
  if (!enabled) {
    return null;
  }
  return mode === DEFAULT_DROP_TOKENS_MODE ? null : mode;
});
activeSimilarityDropTokensModeAtom.debugLabel =
  'activeSimilarityDropTokensModeAtom';

// Derived atom for active fields enabled (returns override or null for default)
export const activeSimilarityFieldsEnabledAtom = atom((get) => {
  const enabled = get(similarityWeightsEnabledAtom);
  const fieldsEnabled = get(similarityFieldsEnabledAtom);
  if (!enabled) {
    return null;
  }
  // Check if all fields are enabled (default state)
  const isDefault = Object.entries(fieldsEnabled).every(
    ([key, value]) => DEFAULT_FIELDS_ENABLED[key as SimilarityField] === value
  );
  return isDefault ? null : fieldsEnabled;
});
activeSimilarityFieldsEnabledAtom.debugLabel =
  'activeSimilarityFieldsEnabledAtom';

// =============================================================================
// SFX SIMILARITY DEBUG CONFIGURATION
// =============================================================================

// Persisted atom for SFX weight overrides
export const sfxSimilarityWeightsOverrideAtom =
  atomWithStorage<SfxSimilarityWeights | null>(
    'debugSfxSimilarityWeights',
    null
  );
sfxSimilarityWeightsOverrideAtom.debugLabel =
  'sfxSimilarityWeightsOverrideAtom';

// Persisted atom for SFX field order
export const sfxSimilarityFieldOrderAtom = atomWithStorage<
  SfxSimilarityField[]
>('debugSfxSimilarityFieldOrder', DEFAULT_SFX_FIELD_ORDER);
sfxSimilarityFieldOrderAtom.debugLabel = 'sfxSimilarityFieldOrderAtom';

// Persisted atom for which SFX fields are enabled
export const sfxSimilarityFieldsEnabledAtom = atomWithStorage<
  Record<SfxSimilarityField, boolean>
>('debugSfxSimilarityFieldsEnabled', DEFAULT_SFX_FIELDS_ENABLED);
sfxSimilarityFieldsEnabledAtom.debugLabel = 'sfxSimilarityFieldsEnabledAtom';

// Persisted atom for SFX drop tokens mode
export const sfxSimilarityDropTokensModeAtom = atomWithStorage<DropTokensMode>(
  'debugSfxSimilarityDropTokensMode',
  DEFAULT_DROP_TOKENS_MODE
);
sfxSimilarityDropTokensModeAtom.debugLabel = 'sfxSimilarityDropTokensModeAtom';

// Derived atom for active SFX weights (returns override or null for default)
export const activeSfxSimilarityWeightsAtom = atom((get) => {
  const enabled = get(similarityWeightsEnabledAtom);
  const overrides = get(sfxSimilarityWeightsOverrideAtom);
  return enabled && overrides ? overrides : null;
});
activeSfxSimilarityWeightsAtom.debugLabel = 'activeSfxSimilarityWeightsAtom';

// Derived atom for active SFX field order
export const activeSfxSimilarityFieldOrderAtom = atom((get) => {
  const enabled = get(similarityWeightsEnabledAtom);
  const fieldOrder = get(sfxSimilarityFieldOrderAtom);
  if (!enabled) {
    return null;
  }
  const isDefault =
    fieldOrder.length === DEFAULT_SFX_FIELD_ORDER.length &&
    fieldOrder.every((field, i) => field === DEFAULT_SFX_FIELD_ORDER[i]);
  return isDefault ? null : fieldOrder;
});
activeSfxSimilarityFieldOrderAtom.debugLabel =
  'activeSfxSimilarityFieldOrderAtom';

// Derived atom for active SFX fields enabled
export const activeSfxSimilarityFieldsEnabledAtom = atom((get) => {
  const enabled = get(similarityWeightsEnabledAtom);
  const fieldsEnabled = get(sfxSimilarityFieldsEnabledAtom);
  if (!enabled) {
    return null;
  }
  const isDefault = Object.entries(fieldsEnabled).every(
    ([key, value]) =>
      DEFAULT_SFX_FIELDS_ENABLED[key as SfxSimilarityField] === value
  );
  return isDefault ? null : fieldsEnabled;
});
activeSfxSimilarityFieldsEnabledAtom.debugLabel =
  'activeSfxSimilarityFieldsEnabledAtom';

// Derived atom for active SFX drop tokens mode
export const activeSfxSimilarityDropTokensModeAtom = atom((get) => {
  const enabled = get(similarityWeightsEnabledAtom);
  const mode = get(sfxSimilarityDropTokensModeAtom);
  if (!enabled) {
    return null;
  }
  return mode === DEFAULT_DROP_TOKENS_MODE ? null : mode;
});
activeSfxSimilarityDropTokensModeAtom.debugLabel =
  'activeSfxSimilarityDropTokensModeAtom';
